import { type JanusCtx, type Row, ec, ecLean, handBack, int, itemField, refuse, requiredInt } from '../context.js';
import { type JanusInput } from '../janus.js';
import { dispatchNextSpell } from '../next-spell.js';
import { janusPostRollChangeAbilityResources } from '../charges/adjust-charges.js';
import { summonApCost } from '../summon.js';
import { abilityActionCost } from '../action-cost.js';
import { captureAreaVfx } from '../area-vfx.js';

/** includes/functions/janus/core/template_abilities_roll.php:template_abilities_roll */
export function spendAbilityAction(ctx: JanusCtx, ability: Row, caster: Row): void {
  if (!ctx.read.inCombat(ctx.encounter, caster)) return;
  const type = String(itemField(ability, 'item_type') ?? '');
  const castingTime = ctx.read.value(ability, 'spell_casting_time');
  let required = abilityActionCost(type, castingTime);
  if (required === null) handBack('template_abilities_roll:casting-time');
  const usesReactionSlot = required === 'r';
  if ((ecLean(caster, 'item_type') ?? itemField(caster, 'item_type')) === 'lair' && !usesReactionSlot) required = 1;
  const reaction = usesReactionSlot;
  if (type === 'trap_ability') required = 0;
  const casterEc = ec(caster);
  // TRIGGER MODE (template_abilities_roll.php ~L142): the AP debit no-ops inside a trigger frame, the REACTION-slot debit does not
  // (Reflexive Shift / a reaction-cast riposte still consume it; janus_trigger() gated its availability upstream).
  if (ctx.trigger && !reaction) return;
  if (reaction) { requiredInt(ecLean(caster, 'reaction'), 'template_abilities_roll:reaction-not-held'); casterEc.reaction = 0; }
  else if (type !== 'free_ability' && type !== 'attack_ability' && !ctx.read.has(ability, 'attack_ability_arr')) {
    // Wizard v9 next_summon_ap_override (summon.ts): a summon ability's AP capped by the armed status, which it consumes
    const cost = summonApCost(ctx, ability, caster, Number(required));
    if (requiredInt(ecLean(caster, 'aps'), 'template_abilities_roll:aps-not-held') < cost)
      refuse(`This ${type} requires ${cost} to cast. You only have ${ecLean(caster, 'aps')} remaining`);
    casterEc.aps = requiredInt(ecLean(caster, 'aps'), 'template_abilities_roll:aps-not-held') - cost;
  }
}

export function templateAbilitiesRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  // creature_ability_charge_pool_spends_on_this_use.php exempts turn-recharging abilities outside combat.
  spendAbilityAction(ctx, ability, caster);
  captureAreaVfx(ctx, input, ability, caster);
  // The plain roll dispatch — or, for a volley's projectile cast under an armed Sanguine (the plan copy prepareNextSpellProjectile hands
  // it), the same replay a prepared spell gets (Overwhelming) and the recipient queue its parent's payloads read. No plan = one dispatch.
  dispatchNextSpell(ctx, input, ability, caster, targets);
  janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, undefined, input);
}

/** janus.php's ground persistent-AOE cost pass for the ability-like types (R-144, 2026-10-07): a zone placed on empty ground (no creature
 *  targets) pays its action and resources — template_abilities_roll with its roll dispatch skipped ($skip_dispatch_for_empty_target_paoe:
 *  the zone's rolls resolve when a creature enters or ends its turn there). Before it a menta zone / wall on empty ground paid nothing. */
export const GROUND_AREA_ABILITY_TYPES: ReadonlySet<string> = new Set(['full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability',
  'lair_ability', 'trap_ability', 'menta', 'sanguine', 'potion', 'coating', 'food', 'drink', 'elixir']);
export function templateAbilitiesGroundAreaCost(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  spendAbilityAction(ctx, ability, caster);
  captureAreaVfx(ctx, input, ability, caster);
  janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, undefined, input);
}
